'use client';

import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import ChevronSm from '../../icons/ChevronSm';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';
import Tab, { TabDefinition } from './Tab';

const toPx = (px: number) => `${px}px`;

const scrollOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest', inline: 'nearest' };

type Id = TabDefinition['id'];

type TabBarProps = {
  activeTab: Id;
  alignment?: 'start' | 'full';
  className?: string;
  hideBottomBorder?: boolean;
  indicatorPosition?: 'top' | 'bottom';
  onChange?: (id: Id) => void;
  tabs: TabDefinition[];
  role?: string;
};

const TabBar = ({ activeTab, alignment = 'start', className, hideBottomBorder, indicatorPosition = 'bottom', onChange, tabs, role }: TabBarProps) => {
  const tabRefs = useRef<{ [id: Id]: HTMLElement }>({});
  const activeTabIndicator = useRef<HTMLDivElement>(null);
  const [activeTabElement, setActiveTabElement] = useState<HTMLElement>();
  const tabBar = useRef<HTMLDivElement>(null);
  const leftArrow = useRef<HTMLButtonElement>(null);
  const rightArrow = useRef<HTMLButtonElement>(null);

  const positionActiveTabIndicator = useCallback(() => {
    if (activeTabElement && activeTabIndicator.current) {
      activeTabIndicator.current.style.opacity = '1';
      activeTabIndicator.current.style.width = toPx(activeTabElement.clientWidth);
      activeTabIndicator.current.style.left = toPx(activeTabElement.offsetLeft);

      // Apply the transition style after a short delay so that the initial positioning isn't animated
      if (!activeTabIndicator.current.style.transition) {
        setTimeout(() => {
          if (activeTabElement && activeTabIndicator.current) {
            activeTabIndicator.current.style.transition = 'all 0.4s ease-in-out';
          }
        }, 500);
      }
    } else if (!activeTabElement && activeTabIndicator.current) {
      activeTabIndicator.current.style.opacity = '0';
    }
  }, [activeTabElement]);

  // alignment and tabs should really only change in storybook
  useEffect(positionActiveTabIndicator, [positionActiveTabIndicator, alignment, tabs]);

  const updateArrowButtons = useCallback(() => {
    if (alignment === 'full' || !tabBar.current || !leftArrow.current || !rightArrow.current) {
      return;
    }
    const { clientWidth, scrollWidth, scrollLeft } = tabBar.current;
    if (scrollLeft < 1) {
      leftArrow.current.style.display = 'none';
    } else {
      leftArrow.current.style.display = 'flex';
    }

    if (scrollWidth - clientWidth - scrollLeft > 1) {
      rightArrow.current.style.display = 'flex';
    } else {
      rightArrow.current.style.display = 'none';
    }
  }, [alignment]);

  const handleResize = useCallback(() => {
    positionActiveTabIndicator();
    updateArrowButtons();
  }, [positionActiveTabIndicator, updateArrowButtons]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', debounce(handleResize, 300));

    return () => {
      window.removeEventListener('resize', debounce(handleResize, 300));
    };
  }, [handleResize]);

  // Scroll one "page", but snap to a tab boundary so a tab straddling the edge is never skipped over.
  const onArrowClick = (scrollRight?: boolean) => {
    const container = tabBar.current;
    if (!container) {
      return;
    }
    const { clientWidth, scrollLeft } = container;

    const arrowWidth = Math.max(leftArrow.current?.offsetWidth ?? 0, rightArrow.current?.offsetWidth ?? 0);
    const visibleLeft = scrollLeft + arrowWidth;
    const visibleRight = scrollLeft + clientWidth - arrowWidth;
    const tabElements = tabs.map(tab => tabRefs.current[tab.id]).filter(Boolean);
    const pageStep = scrollRight ? scrollLeft + clientWidth : scrollLeft - clientWidth;

    let target;
    if (scrollRight) {
      // First tab clipped by the right edge; align its left edge clear of the left arrow.
      const next = tabElements.find(el => el.offsetLeft + el.offsetWidth > visibleRight + 1);
      target = next ? next.offsetLeft - arrowWidth : pageStep;
      if (target <= scrollLeft) {
        target = pageStep;
      }
    } else {
      // Last tab clipped by the left edge; align its right edge clear of the right arrow.
      const prev = [...tabElements].reverse().find(el => el.offsetLeft < visibleLeft - 1);
      target = prev ? prev.offsetLeft + prev.offsetWidth - clientWidth + arrowWidth : pageStep;
      if (target >= scrollLeft) {
        target = pageStep;
      }
    }

    container.scrollTo({ behavior: 'smooth', left: target });
  };

  const switchToTab = (tab: TabDefinition) => {
    const tabElement = tabRefs.current[tab.id];
    if (tabElement) {
      tabElement.focus();
      tabElement.scrollIntoView(scrollOptions);
    }
    if (!tab.linkInfo) {
      // Switching to a tab should trigger the onChange, unless its a link because the user would have no way to navigate past that tab
      onChange?.(tab.id);
    }
  };

  const getTabOnKeyDown =
    (index: number): KeyboardEventHandler<HTMLElement> =>
    e => {
      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          if (index > 0) {
            switchToTab(tabs[index - 1]);
          }
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          if (index < tabs.length - 1) {
            switchToTab(tabs[index + 1]);
          }
          break;
        default:
        // do nothing
      }
    };

  const onTabClick = (id: Id) => {
    const tabElement = tabRefs.current[id];
    if (tabElement) {
      tabElement.scrollIntoView(scrollOptions);
    }
    onChange?.(id);
  };

  const renderedTabs = tabs.map((tab, index) => {
    const active = tab.id === activeTab;
    return (
      <Tab
        role={role}
        key={tab.id}
        {...tab}
        active={active}
        indicatorPosition={indicatorPosition}
        onClick={onTabClick}
        onKeyDown={getTabOnKeyDown(index)}
        tabRef={el => {
          if (active) {
            setActiveTabElement(el);
          }
          tabRefs.current[tab.id] = el;
        }}
      />
    );
  });

  return (
    <div
      {...setDenaliAttribute('TabBar')}
      className={classNames(
        className,
        styles.container,
        hideBottomBorder && styles.hideBottomBorder,
        indicatorPosition === 'top' && styles.topIndicator
      )}
    >
      <div role="tablist" className={classNames(styles.tabBar, styles[alignment])} onScroll={debounce(updateArrowButtons, 100)} ref={tabBar}>
        {renderedTabs}
        <div className={classNames(styles.activeTabIndicator, { [styles.topIndicator]: indicatorPosition === 'top' })} ref={activeTabIndicator} />
      </div>
      <button className={styles.leftArrow} onClick={() => onArrowClick()} ref={leftArrow} tabIndex={-1} aria-hidden type="button">
        <ChevronSm orientation="left" />
      </button>
      <button className={styles.rightArrow} onClick={() => onArrowClick(true)} ref={rightArrow} tabIndex={-1} aria-hidden type="button">
        <ChevronSm orientation="right" />
      </button>
    </div>
  );
};

export default TabBar;
